import { isUndefined } from 'lodash'
import { useEffect } from 'react'
import { WallboxWidgetsData } from './use-data-for-wallbox-widgets'
import { useDynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-provider'
import { useMediaQuery, useTheme } from '@mui/system'

/* every installation has at least battery */
const BATTERY_COUNT = 1

/*
 * A hook that returns the amount of widgets to be displayed for an installation and stores the value to localStorage.
 */
const useInstallationWidgetsAmount = ({
  installationId,
  storageId,
  isWidgetDataLoading,
  wallboxWidgetsData,
}: {
  installationId?: string
  storageId?: string
  isWidgetDataLoading: boolean
  wallboxWidgetsData: WallboxWidgetsData
}): number | null => {
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))

  const storedAmount = localStorage.getItem('amountOfWidgetsToBeDisplayed')
  const parsedStoredAmount = storedAmount ? JSON.parse(storedAmount) : null
  const { isDynamicPowerPriceEnabled } = useDynamicPowerPricesEnabledContext()
  const isDynamicPowerPriceInapplicable = !isDynamicPowerPriceEnabled

  const storedAmountForInstallation =
    isUndefined(installationId) || isUndefined(storageId) ? undefined : parsedStoredAmount?.[installationId]?.[storageId]

  useEffect(() => {
    if (!isWidgetDataLoading) {
      const wallboxWidgetsAmount = wallboxWidgetsData.mappedWallboxesForWidget.length || 0
      const weatherAndDppCount = isDynamicPowerPriceInapplicable && upMd ? 1 : 2 // weather widget is only displayed on md+ screens if DPP is not applicable but both widgets are displayed on smaller screens
      const calculatedAmount = BATTERY_COUNT + weatherAndDppCount + wallboxWidgetsAmount

      if (
        (isUndefined(storedAmountForInstallation) || (storedAmountForInstallation && storedAmountForInstallation !== calculatedAmount)) &&
        storageId &&
        installationId
      ) {
        const updatedAmountOfWidgetsToBeDisplayed = {
          ...parsedStoredAmount,
          [installationId]: {
            ...parsedStoredAmount?.[installationId],
            [storageId]: calculatedAmount,
          },
        }
        localStorage.setItem('amountOfWidgetsToBeDisplayed', JSON.stringify(updatedAmountOfWidgetsToBeDisplayed))
      }
    }
  }, [
    parsedStoredAmount,
    storedAmountForInstallation,
    installationId,
    storageId,
    isWidgetDataLoading,
    wallboxWidgetsData,
    isDynamicPowerPriceEnabled,
    isDynamicPowerPriceInapplicable,
    upMd,
  ])

  return storedAmountForInstallation ? Number(storedAmountForInstallation) : null
}

export default useInstallationWidgetsAmount
